import { nextTick, readonly, ref, watch } from 'vue';
import { tryOnScopeDispose } from '@vueuse/core';
import { MODE } from '@/components/base-components/components/drawer/constants';
import type { BaseDrawerMode } from '@/components/base-components/components/drawer/types';
import { SUPPORT_CHAT_INLINE_ID } from '@/components/app/support-chat/constants';
import { getWindowHashParam } from '@/assets/util/url-hash';

type SupportChatOptions = {
    openFromHash?: boolean;
};

type ChatLoadOptions = {
    widgetOpen?: boolean;
};

type OpenOptions = {
    mode?: BaseDrawerMode;
};

function getConversations() {
    return window.HubSpotConversations;
}

function getWidget() {
    return getConversations()?.widget;
}

const ready = ref(Boolean(getWidget()));
const visible = ref(false);
const loading = ref(false);
const mode = ref<BaseDrawerMode>(MODE.PERSISTENT);

let readyHandlerRegistered = false;
let drawerBehaviorRegistered = false;
let chatEventsRegistered = false;

function onWidgetLoaded() {
    loading.value = false;
}

function registerChatEvents() {
    if (chatEventsRegistered) return;
    if (!getConversations()) return;

    chatEventsRegistered = true;

    getConversations()?.on('widgetLoaded', onWidgetLoaded);
}

function onReady() {
    ready.value = Boolean(getWidget());

    registerChatEvents();
}

function registerReadyHandler() {
    if (readyHandlerRegistered) return;

    readyHandlerRegistered = true;

    if (getConversations()) {
        onReady();

        return;
    }

    window.hsConversationsOnReady = [
        ...(window.hsConversationsOnReady ?? []),
        onReady,
    ];
}

function status() {
    return getWidget()?.status();
}

function load(options: ChatLoadOptions = {}) {
    getWidget()?.load(options);
}

function open(options: OpenOptions = {}) {
    mode.value = options.mode ?? MODE.PERSISTENT;
    visible.value = true;
    loading.value = true;
}

function close() {
    visible.value = false;
}

function toggle(options: OpenOptions = {}) {
    if (!visible.value) {
        open(options);

        return;
    }

    visible.value = !visible.value;
}

function remove() {
    getWidget()?.remove();
}

async function loadDrawerChat() {
    if (!visible.value) {
        loading.value = false;

        return;
    }

    loading.value = true;

    await nextTick();

    if (!visible.value) {
        loading.value = false;

        return;
    }

    if (!ready.value) return;

    if (status()?.loaded) {
        loading.value = false;

        return;
    }

    if (!document.getElementById(SUPPORT_CHAT_INLINE_ID)) return;

    load();
}

function registerDrawerBehavior() {
    if (drawerBehaviorRegistered) return;

    drawerBehaviorRegistered = true;

    watch([visible, ready], loadDrawerChat, { immediate: true });
}

function onDrawerClosed() {
    if (visible.value) return;

    loading.value = false;

    remove();
}

function openFromHash() {
    if (getWindowHashParam('openChat') !== 'true') return;

    open();
}

export default function useSupportChat(options: SupportChatOptions = {}) {
    registerReadyHandler();
    registerDrawerBehavior();

    const stopOpenFromHash = options.openFromHash
        ? watch(ready, openFromHash, { immediate: true })
        : null;

    tryOnScopeDispose(() => stopOpenFromHash?.());

    return {
        loading: readonly(loading),
        mode: readonly(mode),
        ready: readonly(ready),
        visible,

        close,
        open,
        onDrawerClosed,
        toggle,
    };
}
